import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAppShell } from '../app-shell';
import { withStyles, WithStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';

import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import GetAppIcon from '@mui/icons-material/GetApp';

import DataService from '../../services/data-service';

interface VendorInfo {
  companyName?: string;
}

interface State {
  loading: boolean;
  stashCard: null;
  error: unknown;
  vendor?: VendorInfo;
}

interface OwnProps {
  id?: string;
}

const mapStateToProps = (state: { auth: { currentUser: unknown } }) => ({
  currentUser: state.auth.currentUser,
});

function mapDispatchToProps() {
  return {};
}

const styles = (theme: Theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
    overflowX: 'auto' as const,
    justifyContent: 'center',
  },
  header2: { color: '#888888' },
  devices: {
    width: '400px',
    float: 'right' as const,
  },
  installButton: {
    margin: '10px',
  },
  getAppIcon: {
    marginLeft: '10px',
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & WithStyles<typeof styles> & OwnProps;

class VendorPage extends React.Component<Props, State> {
  override state: State = {
    loading: false,
    stashCard: null,
    error: null,
  };

  load(): void {
    const dataService = new DataService();
    this.setState({ loading: true });
    void dataService
      .getVendor(this.props.id ?? '')
      .then((vendor) => {
        this.setState({ vendor: vendor as VendorInfo, loading: false });
      })
      .catch((error: unknown) => {
        this.setState({ error, loading: false });
      });
  }

  override componentDidMount(): void {
    this.load();
  }

  override render(): React.JSX.Element {
    const { classes } = this.props;

    return (
      <div style={{ overflowX: 'auto', justifyContent: 'center' }}>
        <Paper className={classes.root}>
          <h1 className={classes.header2}>STASH Vendor Share</h1>
          <img src="/images/devices.png" alt="STASH app on devices" className={classes.devices} />
          <Typography variant="body1" color="textPrimary" align="center">
            {this.state.loading && 'Fetching StashCard...'}
            {!!this.state.error && 'ERROR'}
            {this.state.vendor && (
              <div>
                <h3 className={classes.header2}>Welcome to STASH!</h3>
                <h2>{this.state.vendor.companyName}</h2>
                <p>
                  It looks like someone shared <b>{this.state.vendor.companyName}</b> with you
                </p>
                <p>To see the full details, please</p>
                <Button
                  className={classes.installButton}
                  size="large"
                  variant="contained"
                  color="primary"
                  endIcon={<GetAppIcon />}
                  target="_blank"
                  href="https://apps.apple.com/us/app/stash-social/id1595856058"
                >
                  Install the STASH Social App
                </Button>
              </div>
            )}
          </Typography>
        </Paper>
      </div>
    );
  }
}

const VendorPageWithStyles = withStyles(styles)(VendorPage);
const VendorPageWithParams = (props: PropsFromRedux) => {
  const params = useParams();
  const { setBreadcrumb } = useAppShell();
  useEffect(() => {
    setBreadcrumb(['Vendor']);
    return () => setBreadcrumb([]);
  }, [setBreadcrumb]);
  return <VendorPageWithStyles {...props} {...params} />;
};

export default connector(VendorPageWithParams);
